import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import UsePerms from '../../context/userAccess';
import useCompanyData from '../../context/company';
import surveyService from '../../services/data/survey.service';
import SurveyTriggerDialog from './SurveyTriggerDialog';

const PERIOD_LABEL = { days: 'dias', months: 'meses', years: 'anos' };
const REF_LABEL = {
    admission_date: 'data de admissão',
    role_start_date: 'início no cargo atual',
};
const SCOPE_LABEL = {
    all: 'Todos os colaboradores',
    filial: 'Filial',
    departamento: 'Departamento',
    cargo: 'Cargo',
};

function errDetail(err) {
    const d = err.response?.data?.detail;
    if (Array.isArray(d)) return d.join(' ');
    if (typeof d === 'string') return d;
    return err.message || 'Erro';
}

function triggerSummary(t) {
    const period = `${t.periodo_valor} ${PERIOD_LABEL[t.periodo_unidade] || t.periodo_unidade}`;
    const ref = REF_LABEL[t.data_referencia] || t.data_referencia;
    const tipo = t.tipo_disparo === 'recurring'
        ? `Recorrente (a cada ${t.recorrencia_valor} ${PERIOD_LABEL[t.recorrencia_unidade] || ''})`
        : 'Uma vez';
    const escopo = SCOPE_LABEL[t.escopo_tipo] || t.escopo_tipo;
    return { period, ref, tipo, escopo };
}

export default function SurveyTriggerSection({ surveyId, surveyStatus, toastRef }) {
    const VerifyPerms = UsePerms();
    const { rootUser } = useCompanyData();
    const companyId = localStorage.getItem('companyId');
    const canEdit = VerifyPerms(['add_survey', 'change_survey']) || rootUser;

    const [autoEnabled, setAutoEnabled] = useState(false);
    const [triggers, setTriggers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [toggling, setToggling] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = useCallback(() => {
        if (!companyId || !surveyId) return;
        setLoading(true);
        Promise.all([
            surveyService.listTriggers({ company_id: companyId, survey_id: surveyId }),
            surveyService.getSurveyDetail({ id: surveyId, company_id: companyId }).catch(() => null),
        ])
            .then(([trigs, detail]) => {
                setTriggers(trigs || []);
                if (detail?.auto_dispatch_enabled !== undefined) {
                    setAutoEnabled(detail.auto_dispatch_enabled);
                }
            })
            .catch((err) => {
                toastRef?.current?.show({ severity: 'error', summary: 'Erro', detail: errDetail(err) });
            })
            .finally(() => setLoading(false));
    }, [companyId, surveyId, toastRef]);

    useEffect(() => { load(); }, [load]);

    function toggleAuto(enabled) {
        if (!canEdit || surveyStatus !== 'AC') return;
        setToggling(true);
        surveyService.toggleAutoDispatch({ company_id: companyId, survey_id: surveyId, enabled })
            .then((r) => {
                setAutoEnabled(r.auto_dispatch_enabled);
                toastRef?.current?.show({
                    severity: 'success',
                    summary: 'Sucesso',
                    detail: r.auto_dispatch_enabled ? 'Disparo automático ativado.' : 'Disparo automático desativado.',
                });
            })
            .catch((err) => {
                toastRef?.current?.show({ severity: 'error', summary: 'Erro', detail: errDetail(err) });
            })
            .finally(() => setToggling(false));
    }

    function handleDelete(trigger) {
        if (!window.confirm('Excluir este gatilho?')) return;
        surveyService.deleteTrigger({ company_id: companyId, trigger_id: trigger.id })
            .then(() => {
                toastRef?.current?.show({ severity: 'success', summary: 'Sucesso', detail: 'Gatilho excluído.' });
                load();
            })
            .catch((err) => {
                toastRef?.current?.show({ severity: 'error', summary: 'Erro', detail: errDetail(err) });
            });
    }

    function openCreate() {
        setEditing(null);
        setDialogOpen(true);
    }

    function openEdit(trigger) {
        setEditing(trigger);
        setDialogOpen(true);
    }

    if (loading) {
        return <div className="text-muted py-3">Carregando gatilhos…</div>;
    }

    return (
        <div className="bg-white rounded border-clean p-4" style={{ marginBottom: '200px' }}>
            {surveyStatus !== 'AC' && (
                <div className="alert alert-info small">
                    Ative a pesquisa antes de configurar disparos automáticos.
                </div>
            )}

            <div className="d-flex justify-content-start gap-4 align-items-center mb-4">
                {canEdit && surveyStatus === 'AC' && autoEnabled && (
                    <button type="button" className="btn btn-success btn-rounded btn-sm px-3" onClick={openCreate}>
                        <FontAwesomeIcon icon={solid('plus')} className="me-1" /> Adicionar gatilho
                    </button>
                )}
                {autoEnabled ? (
                    <span className="fw-bold text-default small">
                        <FontAwesomeIcon icon={solid('bolt')} className="me-1 text-info" />
                        Disparo automático ativo
                    </span>
                ) : (
                    <div className="form-check form-switch">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="autoDispatchToggle"
                            checked={autoEnabled}
                            disabled={!canEdit || toggling || surveyStatus !== 'AC'}
                            onChange={(e) => toggleAuto(e.target.checked)}
                        />
                        <label className="form-check-label fw-bold" htmlFor="autoDispatchToggle">
                            Ativar disparo automático
                        </label>
                    </div>
                )}
                
            </div>

            {triggers.length === 0 ? (
                <p className="text-muted mb-0">Nenhum gatilho configurado.</p>
            ) : (
                <div className="d-flex flex-column gap-3">
                    {triggers.map((t) => {
                        const s = triggerSummary(t);
                        return (
                            <div key={t.id} className="border rounded p-3">
                                <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                        <div className="fw-bold text-default">
                                            Disparar após {s.period} da {s.ref}
                                            {!t.ativo && (
                                                <span className="badge bg-secondary ms-2">Inativo</span>
                                            )}
                                        </div>
                                        <div className="small text-muted mt-1">
                                            Tipo: {s.tipo} | Escopo: {s.escopo}
                                        </div>
                                        <div className="small text-muted">
                                            Prazo: {t.prazo_resposta_dias} dias | Lembrete: {t.lembrete_dias_antes} dias antes
                                            | Tolerância: {t.janela_tolerancia_dias} dia(s) útil(is)
                                        </div>
                                    </div>
                                    {canEdit && (
                                        <div className="text-nowrap">
                                            <button type="button" className="btn btn-sm btn-outline-secondary btn-rounded me-1" onClick={() => openEdit(t)}>
                                                Editar
                                            </button>
                                            <button type="button" className="btn btn-sm btn-outline-danger btn-rounded" onClick={() => handleDelete(t)}>
                                                Excluir
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <SurveyTriggerDialog
                visible={dialogOpen}
                onHide={() => setDialogOpen(false)}
                companyId={companyId}
                surveyId={surveyId}
                trigger={editing}
                onSaved={() => { setDialogOpen(false); load(); }}
                toastRef={toastRef}
            />
        </div>
    );
}
